import { Outlet, useLocation } from 'react-router';
import { Spinner } from './ui/Icon.js';
import { Sidebar } from './Sidebar.js';
import { ShellBar } from './ShellBar.js';
import { ToastProvider } from './Toast.js';
import { FeedbackWidget } from './FeedbackWidget.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { useBrand } from '../lib/brand-context.js';
import { LayoutWidthProvider, useLayoutWidth } from '../lib/layout-width.js';
import { BreadcrumbProvider } from '../lib/breadcrumb-context.js';
import { useDrawer } from '../hooks/use-drawer.js';

function MainContent() {
  const { error, loading } = useBrand();
  const location = useLocation();
  const { contentWidth } = useLayoutWidth();

  return (
    <>
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className={contentWidth === 'full' ? 'w-full' : 'mx-auto w-full max-w-7xl'}>
        {/* Spec 165: gate routed pages on the brand-context's initial load so
            brand-scoped pages don't flash their "Select a brand" empty state
            before the saved brand resolves (localStorage + GET /brands).
            `loading` is true ONLY during that one-time initial fetch (set false
            once selectedBrand is resolved), so this shows a brief skeleton on
            first load, then never again — a single-point fix for every page. */}
        {loading ? (
          <div
            className="flex h-64 flex-col items-center justify-center gap-3 text-[var(--hub-color-ink-subtle)]"
            data-testid="brand-loading-gate"
          >
            <Spinner className="text-violet-500" />
            <p className="text-sm">Loading…</p>
          </div>
        ) : (
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        )}
      </div>
    </>
  );
}

export function Layout() {
  const drawer = useDrawer();
  return (
    <ToastProvider>
      <LayoutWidthProvider>
        <BreadcrumbProvider>
          <div className="hub-shell" data-testid="hub-shell">
            <Sidebar drawerOpen={drawer.open} navRef={drawer.panelRef} />
            <ShellBar
              onOpenDrawer={drawer.toggle}
              drawerTriggerRef={drawer.triggerRef}
              drawerOpen={drawer.open}
            />
            <main className="hub-shell__main" data-testid="shell-main">
              <MainContent />
            </main>
            {/* Spec 229 — the drawer's backdrop. An overlay covering the page
                needs to say so: without it the nav appears to float over live
                content with no indication that the content behind is inert.
                It also gives the click-outside dismissal a visible target
                rather than a dead region.

                Rendered from the shell rather than from inside the nav (where
                the pre-229 version lived) so the panel itself stays a plain
                grid area, and only below 720px — above that the nav is a
                region of the sheet, not an overlay, and there is nothing to
                dim. Tinted with `ink` rather than a literal black so it tracks
                the theme. */}
            {drawer.open && (
              <div
                className="fixed inset-0 z-30 bg-[color-mix(in_oklab,var(--hub-color-ink)_45%,transparent)] hub-compact-only"
                data-testid="drawer-backdrop"
                aria-hidden="true"
                onClick={drawer.close}
              />
            )}
            <FeedbackWidget />
          </div>
        </BreadcrumbProvider>
      </LayoutWidthProvider>
    </ToastProvider>
  );
}
